import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { motion } from 'framer-motion';
import DashboardLayout from '../../components/dashboard/DashboardLayout';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import creditService from '../../utils/creditService';
import campaignServiceEnhanced from '../../utils/campaignServiceEnhanced';
import apolloService from '../../utils/apolloService';

function NewCampaignPage() {
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [creditBalance, setCreditBalance] = useState(0);
    const [creatingCampaign, setCreatingCampaign] = useState(false);

    const [campaignForm, setCampaignForm] = useState({
        name: '',
        description: '',
        subject: '',
        message_template: '',
        target_job_titles: [],
        target_industries: [],
        target_locations: [],
        target_company_sizes: [],
        max_leads_to_fetch: 50,
    });

    const [targetAudienceInput, setTargetAudienceInput] = useState('');

    const { user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        loadCreditBalance();
    }, [user?.id]);

    const loadCreditBalance = async () => {
        if (user?.id) {
            try {
                const result = await creditService.getCreditBalance(user.id);
                if (result?.success) {
                    setCreditBalance(result.data.credits);
                }
            } catch (err) {
                console.error('Error loading credit balance:', err);
            }
        }
    };

    const handleCreateCampaign = async (e) => {
        e.preventDefault();

        if (!campaignForm.name.trim()) {
            setError('Campaign name is required');
            return;
        }

        if (!campaignForm.message_template.trim()) {
            setError('Message template is required');
            return;
        }

        if (creditBalance < 20) {
            setError(`Insufficient credits. You need 20 credits minimum (current: ${creditBalance})`);
            return;
        }

        try {
            setCreatingCampaign(true);
            setError('');

            let oauthToken = null;

            // Deduct campaign creation credits (20 credits)
            const deductResult = await creditService.deductCredits(
                user.id,
                20,
                `Campaign: ${campaignForm.name}`
            );

            if (!deductResult.success) {
                setError('Failed to deduct credits');
                return;
            }

            // Convert the flat target audience input to something Apollo API mock expects (or leave it)
            const finalForm = {
                ...campaignForm,
                target_job_titles: targetAudienceInput ? [targetAudienceInput] : [],
            };

            // Create campaign in database
            const campaignResult = await campaignServiceEnhanced.createCampaign({
                user_id: user.id,
                ...finalForm,
                campaign_status: 'fetching_leads',
                credits_used: 20,
            });

            if (!campaignResult.success) {
                // Refund credits if campaign creation fails
                await creditService.addCredits(user.id, 20, null, 20.00);
                setError(campaignResult.error);
                return;
            }

            const newCampaignId = campaignResult.data.id;

            setSuccess(`Campaign "${campaignResult.data.name}" created! Fetching leads...`);

            // Fetch leads automatically in background
            const leadsResult = await apolloService.fetchAndSaveLeads(
                newCampaignId,
                user.id,
                finalForm,
                oauthToken
            );

            if (leadsResult.success) {
                console.log(`✓ Imported ${leadsResult.data.leadsCreated} leads`);
                await campaignServiceEnhanced.updateCampaignStatus(newCampaignId, 'active');
            } else {
                console.error('Failed to fetch leads:', leadsResult.error);
                await campaignServiceEnhanced.updateCampaignStatus(newCampaignId, 'ready_to_start');
            }

            // Navigate to campaigns screen after a short delay
            setTimeout(() => {
                navigate(`/campaigns/${newCampaignId}`);
            }, 1500);

        } catch (err) {
            console.error('Error creating campaign:', err);
            setError('Failed to create campaign. Please try again.');
        } finally {
            setCreatingCampaign(false);
        }
    };

    return (
        <DashboardLayout title="New Campaign" currentPath="/campaigns/new">
            <div className="max-w-3xl mx-auto space-y-6">
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center justify-between"
                >
                    <div>
                        <Button variant="ghost" className="mb-4 pl-0" onClick={() => navigate('/campaigns')}>
                            <Icon name="ArrowLeft" size={20} className="mr-2" />
                            Back to Campaigns
                        </Button>
                        <h1 className="text-3xl font-headline-bold text-foreground mb-2">Create New Campaign</h1>
                        <p className="text-muted-foreground font-body">
                            Set up your campaign and targeting in one simple step.
                        </p>
                    </div>
                </motion.div>

                {error && (
                    <motion.div className="glassmorphism rounded-lg p-4 border border-error/20 bg-error/10">
                        <p className="text-sm text-error font-body-medium">{error}</p>
                    </motion.div>
                )}

                {success && (
                    <motion.div className="glassmorphism rounded-lg p-4 border border-success/20 bg-success/10">
                        <p className="text-sm text-success font-body-medium">{success}</p>
                    </motion.div>
                )}

                <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    className="glassmorphism rounded-xl border border-border w-full shadow-lg"
                >
                    <form onSubmit={handleCreateCampaign} className="p-8 space-y-8">
                        <div className="space-y-6">
                            {/* Campaign Name */}
                            <div>
                                <label className="block text-sm font-body-semibold text-foreground mb-2">
                                    Campaign Name *
                                </label>
                                <Input
                                    type="text"
                                    placeholder="e.g., Q4 Decision Makers"
                                    value={campaignForm.name}
                                    onChange={(e) => setCampaignForm({ ...campaignForm, name: e.target.value })}
                                    required
                                />
                            </div>

                            {/* Description */}
                            <div>
                                <label className="block text-sm font-body-semibold text-foreground mb-2">
                                    Internal Description
                                </label>
                                <textarea
                                    placeholder="Notes for yourself..."
                                    value={campaignForm.description}
                                    onChange={(e) => setCampaignForm({ ...campaignForm, description: e.target.value })}
                                    className="w-full bg-input border border-border rounded-lg px-4 py-3 text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                                    rows="2"
                                />
                            </div>

                            {/* Target Audience */}
                            <div>
                                <label className="block text-sm font-body-semibold text-foreground mb-2">
                                    Target Audience (Optional)
                                </label>
                                <Input
                                    type="text"
                                    placeholder="e.g., Software Engineers in New York"
                                    value={targetAudienceInput}
                                    onChange={(e) => setTargetAudienceInput(e.target.value)}
                                    className="w-full"
                                />
                                <p className="text-xs text-muted-foreground mt-2">
                                    Describe who you want to reach. Our system will find the best matches.
                                </p>
                            </div>

                            {/* Message Template */}
                            <div>
                                <label className="block text-sm font-body-semibold text-foreground mb-2">
                                    Message Template *
                                </label>
                                <textarea
                                    placeholder="Hi {{firstName}}, I saw you work at {{company}}..."
                                    value={campaignForm.message_template}
                                    onChange={(e) => setCampaignForm({ ...campaignForm, message_template: e.target.value })}
                                    className="w-full bg-input border border-border rounded-lg px-4 py-3 text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none font-mono text-sm"
                                    rows="6"
                                    required
                                />
                            </div>

                            {/* Quantity */}
                            <div>
                                <label className="block text-sm font-body-semibold text-foreground mb-2">
                                    Leads to Reach
                                </label>
                                <select
                                    value={campaignForm.max_leads_to_fetch}
                                    onChange={(e) => setCampaignForm({ ...campaignForm, max_leads_to_fetch: parseInt(e.target.value) })}
                                    className="w-full md:w-1/3 bg-input border border-border rounded-lg px-4 py-3 text-foreground focus:outline-none focus:ring-2 focus:ring-primary appearance-none cursor-pointer"
                                >
                                    <option value={20}>20 Leads</option>
                                    <option value={50}>50 Leads</option>
                                    <option value={100}>100 Leads</option>
                                </select>
                            </div>
                        </div>

                        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 flex items-center justify-between mt-8">
                            <p className="text-sm text-foreground">
                                <Icon name="Info" size={16} className="inline mr-2 text-primary" />
                                This campaign will cost <span className="font-body-semibold">20 credits</span> (Balance: {creditBalance})
                            </p>
                        </div>

                        <div className="flex items-center justify-end gap-4 pt-4 border-t border-border">
                            <Button
                                type="button"
                                variant="ghost"
                                onClick={() => navigate('/campaigns')}
                                disabled={creatingCampaign}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                className="cta-button shadow-lg shadow-primary/25 px-8"
                                disabled={creatingCampaign || creditBalance < 20}
                                iconName={creatingCampaign ? "Loader2" : "Rocket"}
                                iconPosition="left"
                            >
                                {creatingCampaign ? 'Launching...' : 'Launch Campaign'}
                            </Button>
                        </div>
                    </form>
                </motion.div>
            </div>
        </DashboardLayout>
    );
}

export default NewCampaignPage;
